import { useEffect, useState } from "react";
import { formatCad, type Client, type StatementView } from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { useToastStore } from "../../../core/stores/toast";
import { getStatement, sendStatement, statementHtmlUrl, statementPdfUrl } from "../lib/api";
import { addDays, dayKey } from "../lib/dates";
import { EmailSendForm, sendOutcomeToast } from "./email-send-form";
import { InvoiceRow } from "./invoice-row";

interface StatementModalProps {
  open: boolean;
  client: Pick<Client, "id" | "name" | "email">;
  onClose: () => void;
}

/** Bornes ISO d'une période saisie en jours locaux : [début du premier, fin du dernier]. */
function isoRange(from: string, to: string): { from?: string; to?: string } {
  const start = from ? new Date(`${from}T00:00:00`) : null;
  const end = to ? new Date(`${to}T23:59:59.999`) : null;
  return { from: start?.toISOString(), to: end?.toISOString() };
}

/**
 * État de compte d'un client (concessionnaire, flotte ou particulier) : la
 * période, le résumé, les factures listées, puis imprimer, PDF ou envoyer par
 * courriel. Les factures non soldées sont toujours listées, même anciennes.
 */
export function StatementModal({ open, client, onClose }: StatementModalProps) {
  const push = useToastStore((s) => s.push);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [statement, setStatement] = useState<StatementView | null>(null);
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = new Date();
    setFrom(dayKey(addDays(today, -90)));
    setTo(dayKey(today));
    setSending(false);
  }, [open, client.id]);

  useEffect(() => {
    if (!open || !from || !to) return;
    let cancelled = false;
    const range = isoRange(from, to);
    getStatement(client.id, range.from, range.to)
      .then((s) => {
        if (cancelled) return;
        setStatement(s);
        setFailed(false);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, client.id, from, to]);

  const range = isoRange(from, to);

  const send = async (address: string, message: string) => {
    setWorking(true);
    try {
      const result = await sendStatement(client.id, {
        to: address,
        message: message || null,
        periodFrom: range.from,
        periodTo: range.to,
      });
      const toast = sendOutcomeToast(result.status, result.to);
      push(toast.level, toast.title, toast.detail);
      setSending(false);
    } catch {
      push("danger", "Envoi impossible");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Modal open={open} title={`État de compte — ${client.name}`} onClose={onClose} size="lg">
      <div className="statement">
        <div className="statement__period">
          <Field label="Du">
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Au">
            <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <p className="statement__hint">
            Les factures non payées sont toujours incluses, même avant cette période.
          </p>
        </div>

        {failed ? (
          <p className="empty">État de compte indisponible — réessayez dans un instant.</p>
        ) : !statement ? (
          <p className="empty">Chargement…</p>
        ) : (
          <>
            <dl className="atelier-widget__stats statement__stats">
              <div className="atelier-widget__stat">
                <dt className="atelier-widget__label">Factures</dt>
                <dd className="atelier-widget__value">{statement.totals.count}</dd>
              </div>
              <div className="atelier-widget__stat">
                <dt className="atelier-widget__label">Facturé</dt>
                <dd className="atelier-widget__value">{formatCad(statement.totals.totalCents)}</dd>
              </div>
              <div className="atelier-widget__stat">
                <dt className="atelier-widget__label">Solde dû</dt>
                <dd
                  className={`atelier-widget__value${
                    statement.totals.overdueCents > 0
                      ? " is-late"
                      : statement.totals.dueCents === 0
                        ? " is-paid"
                        : ""
                  }`}
                >
                  {formatCad(statement.totals.dueCents)}
                </dd>
              </div>
            </dl>
            {statement.totals.overdueCount > 0 ? (
              <p className="statement__late">
                <Icon name="alert-circle" size={16} />
                {statement.totals.overdueCount} facture
                {statement.totals.overdueCount > 1 ? "s" : ""} en souffrance pour{" "}
                {formatCad(statement.totals.overdueCents)}.
              </p>
            ) : null}
            {statement.invoices.length === 0 ? (
              <p className="empty">Aucune facture pour la période, aucun solde dû.</p>
            ) : (
              <div className="job-list" role="list">
                {statement.invoices.map((inv) => (
                  <InvoiceRow
                    key={inv.id}
                    invoice={inv}
                    showClient={false}
                    onOpen={() => window.open(`/factures?facture=${inv.id}`, "_self")}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {sending ? (
          <EmailSendForm
            defaultTo={client.email}
            sending={working}
            what="l'état de compte"
            onSend={(address, message) => void send(address, message)}
            onCancel={() => setSending(false)}
          />
        ) : null}

        <div className="invoice-detail__actions">
          <Button
            variant="ghost"
            size="sm"
            icon={<Icon name="printer" size={16} />}
            disabled={!statement}
            onClick={() => window.open(statementHtmlUrl(client.id, range.from, range.to), "_blank")}
          >
            Imprimer
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={<Icon name="file" size={16} />}
            disabled={!statement}
            onClick={() => window.open(statementPdfUrl(client.id, range.from, range.to), "_blank")}
          >
            PDF
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Icon name="message" size={16} />}
            disabled={!statement || sending}
            onClick={() => setSending(true)}
          >
            Envoyer par courriel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
